import { HttpRequestDefinition } from '../components/sandBox/SandBox.types';
import { HttpKeyValue } from '../types/rest/dslTypes';

/**
 * Checks if the provided command string is a valid cURL command.
 * A valid cURL command starts with "curl " followed by options and a URL.
 * @param command - The command string to check.
 */
export const isCurlCommand = (command: string): boolean => {
  if (!command) {
    return false;
  }
  return command.trim().startsWith('curl ');
};

/**
 * Parses a cURL command string into an HttpRequestDefinition object.
 * Supports basic cURL syntax including method, headers, data, and URL.
 *
 * @param {string} curlCommand - The cURL command string to parse.
 * @param relativePathOnly - If true, returns only the relative path without the domain.
 * @returns {HttpRequestDefinition} - The parsed HTTP request definition.
 */
export const parseCurlToHttpRequest = (curlCommand: string, relativePathOnly?: boolean): HttpRequestDefinition => {
  const methodRegex = /-X\s+(\w+)/;
  const headerRegex = /-H\s+'([^:]+):\s*([^']+)'/g;
  const dataRegex = /--data\s+'([^']+)'/;
  const urlRegex = /curl\s+['"]([^'"]+)['"]/;

  const methodMatch = curlCommand.match(methodRegex);
  const urlMatch = curlCommand.match(urlRegex);
  const dataMatch = curlCommand.match(dataRegex);

  const headers: HttpKeyValue[] = [];
  let headerMatch: any[];
  while ((headerMatch = headerRegex.exec(curlCommand)) !== null) {
    headers.push({ name: headerMatch[1], value: headerMatch[2] });
  }

  const url = urlMatch ? urlMatch[1] : '';
  const [path, queryString] = url.split('?');
  const queryParameters: HttpKeyValue[] = queryString
    ? queryString.split('&').map(param => {
        const [key, value] = param.split('=');
        return { name: key, value };
      })
    : [];

  let urlPath = path;
  if (relativePathOnly) {
    urlPath = path.replace(/^(?:https?:\/\/)?[^/]+/, '');
  }

  return {
    method: methodMatch ? methodMatch[1] : 'GET',
    path: urlPath || '',
    headers,
    queryParameters,
    body: dataMatch ? dataMatch[1] : '',
  };
};
